import { useTranslation } from '../../i18n';

interface StatusHistoryEntry {
  id: string;
  status_id: string;
  created_at: string;
  notes?: string | null;
  changed_by?: string | null;
}

interface StatusConfigEntry {
  label: string;
  position: number;
}

type StatusConfig = Record<string, StatusConfigEntry> | null;

interface StatusTimelineClientProps {
  history: StatusHistoryEntry[];
  statusConfig: StatusConfig;
  isLoading?: boolean;
}

const DEFAULT_STEPS = [
  { key: 'received', label: 'Received' },
  { key: 'in-repair', label: 'In repair' },
  { key: 'ready', label: 'Ready for delivery' },
];

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

export default function StatusTimelineClient({
  history,
  statusConfig,
  isLoading = false,
}: StatusTimelineClientProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex gap-2 py-4" data-testid="timeline-skeleton">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center flex-1">
            <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse" />
            {i < 3 && <div className="flex-1 h-1 bg-gray-200 mx-2" />}
          </div>
        ))}
      </div>
    );
  }

  // If no history, show empty state
  // If no history AND no statusConfig, show single-step empty state
  if (history.length === 0 && !statusConfig) {
    return (
      <div className="py-4 text-center">
        <p className="text-nb-text-muted text-sm">{t('tracking.statusReceived')}</p>
        <p className="text-nb-text-muted text-xs mt-1">{t('tracking.noStatusUpdates')}</p>
      </div>
    );
  }

  // Build the timeline from history entries
  const sortedHistory = [...history].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );

  // When statusConfig is NULL, fall back to default 3-step labels
  const steps = sortedHistory.map((entry, index) => {
    const config = statusConfig?.[entry.status_id];
    const label = config?.label || DEFAULT_STEPS[index % DEFAULT_STEPS.length].label;
    const isCurrent = index === sortedHistory.length - 1;

    return {
      id: entry.id,
      label,
      date: entry.created_at,
      notes: entry.notes,
      isCurrent,
    };
  });

  // If statusConfig is NULL and we have history entries, but fewer than 3,
  // add remaining default steps to reach 3
  if (!statusConfig && steps.length > 0 && steps.length < 3) {
    const existingLabels = steps.map(s => s.label);
    const missingLabels = DEFAULT_STEPS
      .map(s => s.label)
      .filter(label => !existingLabels.includes(label));

    for (let i = 0; i < missingLabels.length && steps.length < 3; i++) {
      steps.push({
        id: `default-${i}`,
        label: missingLabels[i],
        date: '',
        notes: null,
        isCurrent: false,
      });
    }
  }

  return (
    <div className="space-y-3">
      {steps.map((step, index) => {
        return (
          <div key={step.id} className="flex items-start gap-3">
            <div className="flex flex-col items-center">
              <div
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2
                  ${step.isCurrent
                    ? 'border-nb-black bg-nb-black text-white'
                    : 'border-nb-black bg-white text-nb-black'
                  }
                `}
              >
                ✓
              </div>
              {index < steps.length - 1 && (
                <div className="w-0.5 h-8 bg-nb-black mt-1" />
              )}
            </div>
            <div className="flex-1 pt-1">
              <p className={`font-medium text-sm ${step.isCurrent ? 'text-nb-black font-bold' : 'text-black'}`}>
                {step.label}
              </p>
              {step.date && (
                <p className="text-xs text-nb-text-muted">{formatDate(step.date)}</p>
              )}
              {step.notes && (
                <p className="text-xs text-nb-text-secondary mt-1">{step.notes}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}